import { api } from './api'
import { WaTemplate, WaTemplateType, PaginatedResponse } from '@/types'

export interface WaTemplateFilters {
  search?: string
  type?: WaTemplateType
  isActive?: boolean
  page?: number
  limit?: number
}

export interface CreateWaTemplateRequest {
  name: string
  content: string
  type?: WaTemplateType
  mediaUrl?: string
  variables?: { name: string; defaultValue?: string }[]
  isActive?: boolean
}

export interface UpdateWaTemplateRequest {
  name?: string
  content?: string
  type?: WaTemplateType
  mediaUrl?: string
  variables?: { name: string; defaultValue?: string }[]
  isActive?: boolean
}

export interface RenderTemplateRequest {
  templateId: number
  variables: Record<string, string>
}

export const waTemplateService = {
  async fetchTemplates(filters: WaTemplateFilters = {}): Promise<PaginatedResponse<WaTemplate>> {
    const response = await api.get<{
      success: boolean
      data: WaTemplate[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/whatsapp/templates', { params: filters })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  },

  async getTemplate(id: number): Promise<WaTemplate> {
    const response = await api.get<{ success: boolean; data: WaTemplate }>(`/whatsapp/templates/${id}`)
    return response.data.data
  },

  async createTemplate(data: CreateWaTemplateRequest): Promise<WaTemplate> {
    const response = await api.post<{ success: boolean; data: WaTemplate }>('/whatsapp/templates', data)
    return response.data.data
  },

  async updateTemplate(id: number, data: UpdateWaTemplateRequest): Promise<WaTemplate> {
    const response = await api.put<{ success: boolean; data: WaTemplate }>(`/whatsapp/templates/${id}`, data)
    return response.data.data
  },

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/whatsapp/templates/${id}`)
  },

  async duplicateTemplate(id: number, newName?: string): Promise<WaTemplate> {
    const response = await api.post<{ success: boolean; data: WaTemplate }>(
      `/whatsapp/templates/${id}/duplicate`,
      { name: newName }
    )
    return response.data.data
  },

  async renderPreview(data: RenderTemplateRequest): Promise<{ rendered: string }> {
    const response = await api.post<{ success: boolean; data: { rendered: string } }>(
      '/whatsapp/templates/preview',
      data
    )
    return response.data.data
  },

  async submitForApproval(id: number): Promise<WaTemplate> {
    const response = await api.post<{ success: boolean; data: WaTemplate }>(
      `/whatsapp/templates/${id}/submit`
    )
    return response.data.data
  },

  async syncWithProvider(accountId: number): Promise<{ synced: number; created: number; updated: number }> {
    const response = await api.post<{
      success: boolean
      data: { synced: number; created: number; updated: number }
    }>('/whatsapp/templates/sync', { accountId })
    return response.data.data
  },
}